/**
 * S15 renderer orchestration for the interactive HTML snapshot export.
 * Split out of `dashboardExportService.ts` (which owns definition/PNG/PDF)
 * to keep both files close to their pre-S15 size — reuses that file's
 * `collectCaptureState`/`isDarkTheme` so the header strip and baked theme
 * match the PDF capture exactly.
 *
 * Builds the full HTML string HERE, in the renderer — the data already
 * lives in `useDashboardRuntimeStore` (the same source
 * `TileDataExportMenuItems` reads for CSV/XLSX/JSON) — and hands the
 * finished string to main only for the licensing check + save dialog.
 */
import { isProRequiredError, type ProRequiredError } from '../../../../core/licensing/types';
import type { QueryResultSet } from '../../../../types/queryExecution';
import type { Dashboard } from '../types/dashboardTypes';
import { isQueryBackedTile } from '../types/dashboardTypes';
import { getTileRuntime } from '../store/dashboardRuntimeStore';
import { collectCaptureState, isDarkTheme } from './dashboardExportService';
import { buildHtmlSnapshot } from './htmlSnapshot';

export type HtmlSnapshotExportOutcome =
  | { ok: true }
  | { ok: false; error: string; cancelled?: boolean; proRequired?: ProRequiredError };

/** Query-backed tiles' LAST SUCCESSFUL result — the same data TileDataExportMenuItems reads. */
function collectTileResults(dashboard: Dashboard): Record<string, QueryResultSet | undefined> {
  const resultsByTileId: Record<string, QueryResultSet | undefined> = {};
  for (const tile of dashboard.tiles) {
    if (!isQueryBackedTile(tile)) continue;
    const lastSuccess = getTileRuntime(dashboard.id, tile.id)?.lastSuccess;
    if (lastSuccess) resultsByTileId[tile.id] = lastSuccess.result.selected;
  }
  return resultsByTileId;
}

/**
 * `includeQueries` governs whether each tile's SQL is embedded — connection
 * ids/names and database names are NEVER embedded, regardless (see
 * `services/htmlSnapshot.ts`'s module doc for the full security envelope).
 */
export async function exportDashboardHtmlSnapshot(
  dashboard: Dashboard,
  includeQueries: boolean,
): Promise<HtmlSnapshotExportOutcome> {
  const { params, filters } = collectCaptureState(dashboard);
  const html = buildHtmlSnapshot({
    dashboardName: dashboard.name,
    dashboardDescription: dashboard.description,
    exportedAt: new Date().toISOString(),
    theme: isDarkTheme(),
    includeQueries,
    params,
    filters,
    tiles: dashboard.tiles,
    resultsByTileId: collectTileResults(dashboard),
  });
  const result = await window.electronAPI?.dashboards?.exportHtmlSnapshot({
    html,
    suggestedFileName: `${dashboard.name}.html`,
  });
  if (!result) return { ok: false, error: 'Export is not available in this build' };
  if (isProRequiredError(result)) {
    return { ok: false, error: result.featureDescription, proRequired: result };
  }
  if (!result.success) return { ok: false, error: result.error, cancelled: result.cancelled };
  window.electronAPI?.analytics?.track('feature:dashboard_export', { format: 'html' }).catch(() => {});
  return { ok: true };
}
